import { useState, useEffect } from "react";
import { IconX } from "@tabler/icons-react";
import { toast } from "sonner";
import emailjs from "@emailjs/browser";

type EmailModalProps = {
	isOpen: boolean;
	onClose: () => void;
	recipientEmail: string;
	onSend: (subject: string, content: string) => Promise<void>;
	onEmailSent?: () => Promise<void>;
};

export default function EmailModal({ isOpen, onClose, recipientEmail, onSend, onEmailSent }: EmailModalProps) {
	const [subject, setSubject] = useState("");
	const [content, setContent] = useState("");
	const [sending, setSending] = useState(false);

	useEffect(() => {
		// Initialize EmailJS with your public key
		emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY!);
	}, []);

	if (!isOpen) return null;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			setSending(true);

			await emailjs.send(process.env.REACT_APP_EMAILJS_SERVICE_ID!, process.env.REACT_APP_EMAILJS_TEMPLATE_ID!, {
				to_email: recipientEmail,
				subject: subject,
				message: content,
				from_name: "Sefa Ilyas Öz",
				reply_to: "sio@softwarify.co",
			});

			// Update email sent status
			if (onEmailSent) {
				await onEmailSent();
			}

			toast.success("Email sent successfully");
			onClose();
			setSubject("");
			setContent("");
		} catch (error: any) {
			console.error("Error sending email:", error);
			toast.error(error.message || "Failed to send email");
		} finally {
			setSending(false);
		}
	};

	return (
		<div className='fixed inset-0 z-50 overflow-y-auto'>
			<div className='flex min-h-screen items-center justify-center p-4'>
				{/* Backdrop */}
				<div className='fixed inset-0 bg-black/50 transition-opacity' onClick={onClose} />

				{/* Modal */}
				<div className='relative w-full max-w-2xl rounded-lg bg-white dark:bg-gray-800 p-6 shadow-xl'>
					<div className='flex items-center justify-between mb-4'>
						<h3 className='text-lg font-semibold text-gray-900 dark:text-white'>Compose Email</h3>
						<button onClick={onClose} className='text-gray-400 hover:text-gray-500 dark:hover:text-gray-300'>
							<IconX className='h-5 w-5' />
						</button>
					</div>

					<form onSubmit={handleSubmit} className='space-y-4'>
						<div>
							<label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>To:</label>
							<div className='block w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'>
								{recipientEmail}
							</div>
						</div>

						<div>
							<label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>Subject:</label>
							<input
								type='text'
								value={subject}
								onChange={(e) => setSubject(e.target.value)}
								required
								placeholder='Enter email subject'
								className='block w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
							/>
						</div>

						<div>
							<label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>Message:</label>
							<textarea
								value={content}
								onChange={(e) => setContent(e.target.value)}
								required
								placeholder='Enter your message'
								rows={6}
								className='block w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
							/>
						</div>

						<div className='flex justify-end space-x-3'>
							<button
								type='button'
								onClick={onClose}
								disabled={sending}
								className='rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-transparent px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'>
								Cancel
							</button>
							<button
								type='submit'
								disabled={sending}
								className='rounded-md bg-primary px-4 py-2 text-sm font-medium text-black hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'>
								{sending ? "Sending..." : "Send Email"}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
}
